import {
  getAnalytics,
  isSupported,
  logEvent,
  setAnalyticsCollectionEnabled,
  type Analytics,
} from 'firebase/analytics'
import app, { isFirebaseConfigured } from '@/lib/firebase'

let analytics: Analytics | null = null
let initPromise: Promise<Analytics | null> | null = null

/** GA4 measurement ID is set (Firebase Console → Project settings). */
export function isGa4Configured(): boolean {
  return (
    isFirebaseConfigured() &&
    Boolean(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID?.trim())
  )
}

/**
 * Production only unless VITE_GA4_ENABLED=true — keeps dev traffic out of reports
 * and avoids unnecessary event volume.
 */
export function isGa4Enabled(): boolean {
  if (!isGa4Configured()) return false
  if (import.meta.env.PROD) return true
  return import.meta.env.VITE_GA4_ENABLED === 'true'
}

/** Storefront paths only — admin panel must not appear in GA4 reports. */
export function isStorefrontAnalyticsPath(pathname: string): boolean {
  return !pathname.startsWith('/admin')
}

function applyCollectionForPath(instance: Analytics, pathname: string) {
  setAnalyticsCollectionEnabled(
    instance,
    isStorefrontAnalyticsPath(pathname)
  )
}

export function initGa4(): Promise<Analytics | null> {
  if (!isGa4Enabled()) return Promise.resolve(null)
  if (analytics) return Promise.resolve(analytics)
  if (initPromise) return initPromise

  initPromise = isSupported()
    .then((supported) => {
      if (!supported) return null
      analytics = getAnalytics(app)
      applyCollectionForPath(analytics, window.location.pathname)
      return analytics
    })
    .catch(() => null)

  return initPromise
}

/** Enable or pause GA4 collection when crossing admin ↔ storefront routes. */
export async function syncGa4ForPath(pathname: string): Promise<void> {
  if (!isGa4Enabled()) return
  const instance = await initGa4()
  if (!instance) return
  applyCollectionForPath(instance, pathname)
}

type Ga4Params = Record<string, string | number | boolean>

/** Log a page view for SPA route changes (powers paths, engagement time, DAU in GA4). */
export async function trackPageView(path: string, title?: string) {
  const pathname = path.split('?')[0] || '/'
  if (!isStorefrontAnalyticsPath(pathname)) return

  const instance = await initGa4()
  if (!instance) return

  applyCollectionForPath(instance, pathname)

  logEvent(instance, 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: `${window.location.origin}${path}`,
  })
}

/** Optional storefront events — standard GA4 names, no Firestore writes. */
export async function trackGa4Event(eventName: string, params?: Ga4Params) {
  if (!isStorefrontAnalyticsPath(window.location.pathname)) return

  const instance = await initGa4()
  if (!instance) return

  logEvent(instance, eventName, params)
}
